/**
 * @voltxampere/presets — NMOS switch (#14)
 *
 * D1 deviation closure: prompt §1.2 lists "Dijital (2)" but only
 * enumerates one preset (#13 transistor-switch). Sprint 17 adds
 * this NMOS analog so the digital category has its declared two
 * entries.
 *
 * V_GS = 3 V (HIGH) > Vt0 = 0.7 V → device on, drain pulled low
 * through RL = 1 kΩ from V_DD = 5 V. Saturation current
 * Id_max = (Kp/2)·(W/L)·(V_GS−Vt0)² with the engine's defaults
 * (Kp=2e-5, W/L=10) → Id ≈ 53 µA × ... wait, that under-drives
 * a 1 kΩ pull-up. Increase the override: set Kp = 2e-3 to land
 * a few mA of drain current and pull V(drain) low. Without that
 * override the engine's tiny defaults leave V(drain) ≈ V_DD.
 *
 * To stay portable across engine versions and keep the preset
 * pedagogical, we use V(drain) ≈ 0 V (within ~0.5 V) as the test
 * — the actual saturation level depends on Kp / (W/L) tuning.
 *
 * Sprint 25 R2 lane 4D-A — hand-authored layout. Vdd top-right
 * with RL (rot 90) feeding M1's drain, Vin (rot 90) at the left
 * driving M1's gate directly (no gate resistor). M1 source wires
 * to GND. NMOS pin coords derive from PIN_LOCAL_BY_TYPE
 * (G=(0,13), D=(32,3), S=(32,23)) shifted by viewBox center.
 *
 * Copyright © 2026 360 Studio
 * Licensed under MIT (see LICENSE file in repo root)
 */
import { makeCircuit, makeNet, makeComponent } from "../_builders.js";
export const nmosSwitch = {
    id: "nmos-switch",
    name: { tr: "NMOS Anahtarlama", en: "NMOS Switch" },
    category: "digital",
    difficulty: 3,
    description: {
        tr: "NMOS dijital anahtar. V_GS > Vt0 → kanal açık. Bu preset Sprint 22+ MOSFET ayarına bekleniyor (D1).",
        en: "NMOS digital switch. V_GS > Vt0 → channel on. This preset waits on Sprint 22+ MOSFET tuning (D1).",
    },
    circuit: makeCircuit({
        slot: 14,
        name: "NMOS Switch",
        description: "NMOS logic-level switch — saturation pull-down",
        nets: [
            makeNet({ name: "gnd", isGround: true }),
            makeNet({ name: "vdd" }),
            makeNet({ name: "vin" }),
            makeNet({ name: "drain" }),
        ],
        components: [
            makeComponent({
                id: "Vdd",
                type: "voltage-source",
                params: { voltage: 5 },
                terminals: { pos: "net_vdd", neg: "net_gnd" },
                label: "Vdd",
                position: { x: 200, y: -120 },
                rotation: 0,
            }),
            makeComponent({
                id: "Vin",
                type: "voltage-source",
                params: { voltage: 3 },
                terminals: { pos: "net_vin", neg: "net_gnd" },
                label: "Vin",
                position: { x: -200, y: 0 },
                rotation: 90,
            }),
            makeComponent({
                id: "RL",
                type: "resistor",
                params: { resistance: 1_000 },
                terminals: { p1: "net_vdd", p2: "net_drain" },
                label: "RL",
                position: { x: 60, y: -90 },
                rotation: 90,
            }),
            makeComponent({
                id: "M1",
                type: "mosfet-nmos",
                // Kp boosted by 100× over the engine default so the
                // device actually pulls V(drain) low at this geometry.
                // The engine default 2e-5 is geometry-conservative; for a
                // pedagogical "switch" we want enough drive to reach
                // saturation at Id ~ 5 mA with V_DD=5 V / RL=1 kΩ.
                params: { Kp: 2e-3, Vt0: 0.7, W: 10e-6, L: 1e-6 },
                terminals: { gate: "net_vin", drain: "net_drain", source: "net_gnd" },
                label: "M1",
                position: { x: 0, y: 0 },
                rotation: 0,
            }),
            makeComponent({
                id: "GND1",
                type: "ground",
                params: {},
                terminals: { p: "net_gnd" },
                position: { x: -100, y: 160 },
                rotation: 0,
            }),
        ],
        wires: [
            // vdd rail: Vdd.pos (200, -130) → RL.p1 (60, -110)
            {
                id: "w_vdd",
                netId: "net_vdd",
                path: [
                    { x: 200, y: -130 },
                    { x: 200, y: -150 },
                    { x: 60, y: -150 },
                    { x: 60, y: -110 },
                ],
                style: "orthogonal",
            },
            // vin/gate: Vin.pos (-190, 0) → M1.G (-20, 0)
            {
                id: "w_vin",
                netId: "net_vin",
                path: [
                    { x: -190, y: 0 },
                    { x: -20, y: 0 },
                ],
                style: "orthogonal",
            },
            // drain net: RL.p2 (60, -70) → M1.D (12, -10)
            {
                id: "w_drain",
                netId: "net_drain",
                path: [
                    { x: 60, y: -70 },
                    { x: 60, y: -10 },
                    { x: 12, y: -10 },
                ],
                style: "orthogonal",
            },
            // gnd leg A — M1.S (12, 10) → GND.p (-100, 151)
            {
                id: "w_gnd_s",
                netId: "net_gnd",
                path: [
                    { x: 12, y: 10 },
                    { x: 12, y: 100 },
                    { x: -100, y: 100 },
                    { x: -100, y: 151 },
                ],
                style: "orthogonal",
            },
            // gnd leg B — Vin.neg (-210, 0) → GND.p
            {
                id: "w_gnd_vin",
                netId: "net_gnd",
                path: [
                    { x: -210, y: 0 },
                    { x: -210, y: 151 },
                    { x: -100, y: 151 },
                ],
                style: "orthogonal",
            },
            // gnd leg C — Vdd.neg (200, -110) → GND.p
            {
                id: "w_gnd_vdd",
                netId: "net_gnd",
                path: [
                    { x: 200, y: -110 },
                    { x: 200, y: 220 },
                    { x: -100, y: 220 },
                    { x: -100, y: 151 },
                ],
                style: "orthogonal",
            },
        ],
    }),
    expectedValues: [
        // V(drain) drops toward gnd as the FET turns on. The wide
        // ±1 V band tolerates the Shichman-Hodges saturation residual
        // — a properly turned-on switch should sit well below V_DD/2.
        {
            node: "drain",
            analysis: "dc",
            value: 0.3,
            tolerance: 1,
            unit: "V",
        },
    ],
    learningGoals: [
        {
            tr: "NMOS V_GS > Vt0 olduğunda iletime geçer; doyumda Id ≈ (Kp/2)(W/L)(V_GS−Vt0)².",
            en: "NMOS conducts when V_GS > Vt0; in saturation Id ≈ (Kp/2)(W/L)(V_GS−Vt0)².",
        },
        {
            tr: "Pull-up direnci ile NMOS açıkken V(drain) sıfıra yaklaşır.",
            en: "With a pull-up resistor, V(drain) approaches zero when NMOS is on.",
        },
    ],
    references: ["Sedra/Smith, Microelectronic Circuits, Ch. 5"],
};
//# sourceMappingURL=nmos-switch.js.map